/**
 * WP Consent API
 * https://github.com/rlankhorst/wp-consent-level-api
 * https://wordpress.org/plugins/wp-consent-api/
 */

/** PMW category -> WP Consent API category name. */
const categoryNames = {
	statistics : "statistics",
	marketing  : "marketing",
	preferences: "preferences",
	necessary  : "functional",
}

const cookiePrefix = "wp_consent_"

/**
 * The consent type the CMP on the page has declared. Complianz for example sets
 * window.wp_consent_type from its own consent type.
 *
 * @returns {string}
 */
const consentType = () => window.wp_consent_type || window.wp_fallback_consent_type || ""

/**
 * Whether an unset category may be read as consent, following the plugin's own
 * wp_has_consent(): under opt-out it may, under opt-in only an explicit "allow"
 * counts. Reading an unset category as consent under opt-in would unblock every
 * pixel the CMP never granted.
 *
 * An undeclared consent type means the plugin sees no consent management, and it
 * then grants everything. PMW cannot follow that when explicit consent mode is
 * on: the CMP that declares the type may simply not have run yet when PMW boots,
 * and an unknown must never grant while the site owner has asked for pixels to
 * stay blocked until consent is in.
 *
 * @returns {boolean}
 */
const impliedConsentApplies = () => {

	const type = consentType()

	if (type) return type.indexOf("optout") !== -1

	return !pmw.consent.explicitConsentModeActive()
}

/**
 * @param value {string} - the raw cookie or event value
 * @returns {boolean}
 */
const hasConsent = (value) => {

	if (value === "allow") return true
	if (value !== "") return false

	return impliedConsentApplies()
}

/**
 * Read the four category cookies PMW maps.
 *
 * @returns {{statistics: string, marketing: string, preferences: string, necessary: string}}
 */
const getCategoryCookies = () => {

	let values = {}

	for (const [category, name] of Object.entries(categoryNames)) {
		values[category] = pmw.getCookie(cookiePrefix + name)
	}

	return values
}

export const wpConsentApi = {

	/**
	 * Get the WP Consent API cookie.
	 *
	 * @returns {{statistics: boolean, marketing: boolean, preferences: boolean, necessary: boolean}|null}
	 */
	getConsent: () => {

		const values = getCategoryCookies()

		/**
		 * Only a category cookie carries a visitor decision. Other cookies the
		 * plugin writes, such as wp_consent_consented_services, do not, so their
		 * mere presence must not be read as consent.
		 */
		if (!Object.values(values).some(value => value !== "")) return null

		pmw.console.log("WP Consent API CMP consent detected")

		return {
			statistics : hasConsent(values.statistics),
			marketing  : hasConsent(values.marketing),
			preferences: hasConsent(values.preferences),
			// Functional is always allowed unless it has been denied explicitly.
			necessary  : values.necessary !== "deny",
		}
	},

	/**
	 * WP Consent API
	 * https://github.com/rlankhorst/wp-consent-level-api
	 * https://wordpress.org/plugins/wp-consent-api/
	 */
	loadEventListeners: () => {

		document.addEventListener("wp_listen_for_consent_change", e => {

			/**
			 * wp_set_consent() fires one event per category and its detail carries
			 * only the single category that changed. Categories that are absent
			 * must keep their current value, and categories PMW does not map, such
			 * as Complianz' statistics-anonymous, must be ignored entirely.
			 *
			 * Reading all four categories off a one-category detail turned every
			 * relayed denial into a grant for the other three, because an absent
			 * key is not "deny".
			 */
			const changes = e.detail || {}

			let data = {}

			for (const [category, name] of Object.entries(categoryNames)) {

				if (!(name in changes)) continue

				data[category] = category === "necessary"
					? changes[name] !== "deny"
					: hasConsent(changes[name])
			}

			if (Object.keys(data).length === 0) return

			pmw.consent.api.updateSelectively({...data, source: "cmp"})
		}, true)
	},
}
